import { faCoin } from "@fortawesome/pro-regular-svg-icons";

import { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";

import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";

import { Icon, Modal } from "@/components";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { AiApplyType, CustomColumnTypes, PriorityFilterStatus, UpdateType } from "@/enum/list.enum";

import { InputField } from "@/components/InputField/InputField";

import { useAppSelector } from "@/redux/hooks";

import { ColumnConfig } from "@/pages/Private/pages/List/schema/columnConfig";

import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { useUpdateListColumnsMutation } from "@/pages/Private/redux/list/list.api";

import { Radio } from "@/components/Radio/Radio";

import { Information } from "@/components/Information/Information";

import { CustomColumn, CustomColumnSchema, List } from "../../../schema/list";

import { getCurrentPrices } from "../../../utils/currentPrices";
import { CustomColumnApplyFilters } from "../customColumnApplyFilters";

export interface LinkedinPostEngagementModalProps {
	setShowConfirm: (value: boolean) => void;
	open: boolean;
	listId: number;
	setOpen: (value: boolean) => void;
	columnId?: number;
	column?: ColumnConfig;
	customColumnName?: string;
	filters?: CustomColumnApplyFilters;
	selectedProspectIds?: number[];
	list?: List;
	priorityFilterStatus?: PriorityFilterStatus[];
}

export const LinkedinPostEngagementModal: FunctionComponent<LinkedinPostEngagementModalProps> = ({
	setShowConfirm,
	listId,
	open,
	setOpen,
	columnId,
	column,
	customColumnName,
	filters,
	selectedProspectIds,
	list,
	priorityFilterStatus,
}) => {
	const [applyTo, setApplyTo] = useState<AiApplyType>(AiApplyType.NONE);

	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`aiColumn.${key}`), [t]);

	const [update, { isLoading, error }] = useUpdateListColumnsMutation();
	const { inbox, meta } = useAppSelector(listSelector);

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
		watch,
	} = useForm<CustomColumn>({
		defaultValues: {
			customColumnType: CustomColumnTypes.LINKEDIN_POST_ENGAGEMENT,
			online: false,
			...column,
			customColumnName: customColumnName || column?.name,
			value: column?.config ? JSON.parse(column?.config) : {},
		},
		resolver: zodResolver(CustomColumnSchema),
	});

	const formValues = watch();

	useEffect(() => {
		if (column) {
			setValue("customColumnName", column.name);
			setValue("value", column.config ? JSON.parse(column.config) : {});
		}
	}, [column]);

	const onSubmit = async (values: CustomColumn) => {
		try {
			values.value = JSON.stringify(values.value);

			const prospectIds =
				applyTo === AiApplyType.FIRST_10
					? inbox.slice(0, 10).map((p) => p.id)
					: applyTo === AiApplyType.CURRENTLY_SELECTED
						? selectedProspectIds
						: undefined;

			await update({
				...values,
				id: listId,
				updateType: UpdateType.CUSTOM_COLUMN,
				aiApplyType: applyTo,
				prospectIds: prospectIds,
				columnId,
				filters: applyTo === AiApplyType.CURRENTLY_FILTERED ? filters : undefined,
				priorityFilterStatus: priorityFilterStatus ?? undefined,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const applyToButtonText = useMemo(() => {
		return applyTo === AiApplyType.NONE
			? ts(`button.${AiApplyType.NONE}`)
			: applyTo === AiApplyType.FIRST_10
				? ts(`button.${AiApplyType.FIRST_10}`)
				: applyTo === AiApplyType.CURRENTLY_FILTERED
					? ts(`button.${AiApplyType.CURRENTLY_FILTERED}`)
					: ts(`button.${AiApplyType.ALL}`);
	}, [applyTo, ts]);

	const currentCreditCost = useMemo(() => {
		return getCurrentPrices({
			applyTo,
			meta,
			singlePrice: 1,
			selectedProspectIds,
			list,
		});
	}, [applyTo, getValues("online"), meta.itemCount, selectedProspectIds?.length]);

	const allowSave = useMemo(() => {
		return (
			(formValues.value?.personalProfile || formValues.value?.companyProfile) &&
			(formValues.value?.posts ||
				formValues.value?.likesAndComments ||
				formValues.value?.likesAndCommentsPerPost) &&
			formValues.customColumnName
		);
	}, [formValues.value, formValues.customColumnName]);

	return (
		<Modal
			disableSave={!allowSave}
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			secondSubmitButtonText={t("basics.save")}
			size="md"
			submitButtonText={applyToButtonText}
			title="Analyze Linkedin post engagement"
		>
			<div>
				<div className="flex flex-col items-start">
					<div className="flex relative flex-row w-full items-start gap-4">
						<div className="text-sm font-medium mb-1 w-full">
							<InputField
								error={!!errors.customColumnName}
								handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
									setValue("customColumnName", event.target.value, {
										shouldValidate: true,
										shouldDirty: true,
									});
								}}
								label="Column Name"
								name={"name"}
								placeholder="Enter column name"
								value={getValues("customColumnName")}
							/>
						</div>
					</div>
				</div>
				<div className="flex flex-col items-start mb-4">
					<div className="flex flex-row justify-between w-full">
						<div className="text-sm font-medium mb-2">Analyze Linkedin engagement of</div>
					</div>
					<div className="flex relative flex-row w-full items-start gap-4">
						<div className="flex flex-col gap-2">
							<Radio
								isChecked={formValues.value?.personalProfile || false}
								name="personalProfile"
								onChange={() => {
									setValue(
										"value",
										{
											...formValues.value,
											personalProfile: true,
											companyProfile: false,
										},
										{
											shouldValidate: true,
											shouldDirty: true,
										}
									);
								}}
							>
								Personal profile
							</Radio>
							<Radio
								isChecked={formValues.value?.companyProfile || false}
								name="companyProfile"
								onChange={() => {
									setValue(
										"value",
										{
											...formValues.value,
											personalProfile: false,
											companyProfile: true,
										},
										{
											shouldValidate: true,
											shouldDirty: true,
										}
									);
								}}
							>
								Company profile
							</Radio>
						</div>
					</div>
				</div>
				<div className="flex flex-col items-start">
					<div className="flex flex-row justify-between w-full">
						<div className="text-sm font-medium mb-4">Type of engagement</div>
					</div>
					<div className="flex relative flex-row w-full items-start gap-4">
						<div className="flex flex-col gap-2">
							<Radio
								isChecked={formValues.value?.posts || false}
								name="posts"
								onChange={() => {
									setValue(
										"value",
										{
											...formValues.value,
											posts: true,
											likesAndComments: false,
											likesAndCommentsPerPost: false,
										},
										{
											shouldValidate: true,
											shouldDirty: true,
										}
									);
								}}
							>
								<>
									Amount of posts in the last 30 days{" "}
									<Information information="Amount of posts in the last 30 days" />
								</>
							</Radio>
							<Radio
								isChecked={formValues.value?.likesAndComments || false}
								name="likesAndComments"
								onChange={() => {
									setValue(
										"value",
										{
											...formValues.value,
											posts: false,
											likesAndComments: true,
											likesAndCommentsPerPost: false,
										},
										{
											shouldValidate: true,
											shouldDirty: true,
										}
									);
								}}
							>
								<>
									Total engagement of last 30 days{" "}
									<Information information="If more than 20, then the result in the column should be 'more than 20'" />
								</>
							</Radio>
							<Radio
								isChecked={formValues.value?.likesAndCommentsPerPost || false}
								name="likesAndCommentsPerPost"
								onChange={() => {
									setValue(
										"value",
										{
											...formValues.value,
											posts: false,
											likesAndComments: false,
											likesAndCommentsPerPost: true,
										},
										{
											shouldValidate: true,
											shouldDirty: true,
										}
									);
								}}
							>
								<>
									Amount of likes and comments per post in the last 30 days{" "}
									<Information information="Amount of likes and comments per post in the last 30 days" />
								</>
							</Radio>
						</div>
					</div>
				</div>
			</div>

			<div>
				<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
					<Dropdown
						data={[
							{
								title: ts(AiApplyType.NONE),
								id: AiApplyType.NONE,
							},
							{
								title: ts(AiApplyType.FIRST_10),
								id: AiApplyType.FIRST_10,
							},
							{
								title: ts(AiApplyType.CURRENTLY_FILTERED),
								id: AiApplyType.CURRENTLY_FILTERED,
							},
							{
								title: ts(AiApplyType.CURRENTLY_SELECTED),
								id: AiApplyType.CURRENTLY_SELECTED,
							},
							{
								title: ts(AiApplyType.ALL),
								id: AiApplyType.ALL,
							},
						]}
						floating={false}
						handleSelect={function (value?: AutoCompleteItem) {
							if (value?.id) {
								setApplyTo(value.id as AiApplyType);
							}
						}}
						label="Search posts for"
						value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
					/>
				</div>
			</div>

			<div className="flex flex-row border-t pt-5 border-gray-200 text-gray-700 items-center text-sm mt-4">
				<span className="mr-1">1x</span>
				<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
				<span className="">per result</span>
			</div>
			{applyTo && currentCreditCost > 0 && (
				<div className="flex flex-row pt-5 text-gray-700 items-center text-sm">
					<span className="mr-1">{currentCreditCost}</span>
					<Icon className="w-[16px] h-[16px] text-gray-700 mr-1" icon={faCoin} />
					<span className="">total</span>
				</div>
			)}

			{getAllErrors(error, formErrors).length ? (
				<div className="mt-4 mb-0">{renderErrorMessages(getAllErrors(error, formErrors))}</div>
			) : null}
		</Modal>
	);
};
